import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "How fast can my website be ready?",
    a: "We are built for speed. For select businesses, we may already have a website concept prepared before the call. After approval, same-day launch may be available if we have all required business information, content, and domain access.",
  },
  {
    q: "Can my website really go live the same day?",
    a: "Yes, same-day launch may be available after approval when the website concept is ready, the requested changes are simple, and all required access and information are provided. Larger changes, missing content, domain issues, or advanced features may take longer.",
  },
  {
    q: "Do I need to be tech-savvy?",
    a: "No. We keep the process simple and guide you through what we need.",
  },
  {
    q: "What does the $750 include?",
    a: "It includes the professional website build, mobile-friendly layout, core business sections, calls-to-action, contact info, basic SEO structure, and reasonable revisions before final approval.",
  },
  {
    q: "Does the $750 include hosting?",
    a: "No. The $750 is the one-time website build fee. Hosting, support, maintenance, and future updates are covered through the Website Care & Hosting Plan.",
  },
  {
    q: "What is the Website Care & Hosting Plan?",
    a: "The Website Care & Hosting Plan is $99/month and keeps your website live, hosted, managed, updated, and supported.",
  },
  {
    q: "What updates are included in the Care & Hosting Plan?",
    a: "Reasonable text updates, image updates, service updates, price or menu updates, business hour changes, contact info updates, social media link updates, minor layout adjustments, and general website support.",
  },
  {
    q: "How do I request updates?",
    a: "Just send your requested changes in writing. Once we have the information needed, approved standard updates are completed within 24 business hours or less.",
  },
  {
    q: "Are there any extra costs?",
    a: "Some items may cost extra, such as domain purchases, premium apps, paid plugins, advanced booking systems, e-commerce features, custom integrations, logo design, professional photography, paid ads, or major redesigns.",
  },
  {
    q: "What does unlimited revisions mean?",
    a: "During the website build, it means we will make reasonable design and content changes before final approval so you feel confident with the final website.",
  },
  {
    q: "Can you redesign my current website?",
    a: "Yes. We can refresh your existing website or create a completely new version from scratch.",
  },
  {
    q: "Can you help publish the website?",
    a: "Yes. We can help prepare the site for publishing and guide the domain connection process.",
  },
  {
    q: "Why is the price discounted?",
    a: "Because for select businesses, we may already have a website concept prepared before the call. Since the foundation is already created, we can offer a special discounted build price.",
  },
  {
    q: "Why is this easier than hiring a regular web developer?",
    a: "The usual process requires searching for developers, scheduling meetings, explaining your vision, paying before seeing the final result, waiting weeks for a first draft, and going back and forth with changes. Our process removes most of that friction by showing a concept first and moving quickly once approved.",
  },
  {
    q: "Do I own my website?",
    a: "Yes. You own your website. If you ever cancel the Care & Hosting Plan, hosting and ongoing support stop, but we can hand off the website files or help transfer it to you.",
  },
  {
    q: "Can I cancel the Care & Hosting Plan anytime?",
    a: "Yes. The Care & Hosting Plan is month-to-month. If you cancel, hosting and ongoing updates end, but you keep ownership of your website.",
  },
];

export default function FAQSection() {
  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <div className="text-center fade-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 text-[11px] font-black tracking-widest uppercase">
            <HelpCircle size={12} /> Frequently Asked Questions
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-slate-900">
            Answers to the questions business owners ask most
          </h2>
        </div>

        <Accordion
          type="single"
          collapsible
          data-testid="faq-accordion"
          className="mt-12 rounded-3xl bg-white border border-slate-100 shadow-sm divide-y divide-slate-100 overflow-hidden"
        >
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="px-6 sm:px-8 border-b-0"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left font-display font-bold text-slate-900 text-base sm:text-lg py-5 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i}`}
                className="text-slate-600 text-base leading-relaxed pb-6 pr-2"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
